import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
  Bus, MapPin, Calendar, Users, Search, Star, Clock, 
  Wifi, Wind, Tv, Coffee, ArrowRight, ChevronRight, Ticket,
  ChevronDown, ChevronUp, Navigation, Info, RefreshCw, Bell, BellOff,
  ArrowLeftRight, ArrowLeft
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import api from '../../utils/api';
import LanguageSelector from '../../components/bus/LanguageSelector';
import { useLanguage } from '../../contexts/LanguageContext';

const BusSearch = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [searchParams, setSearchParams] = useState({
    origin: '',
    destination: '',
    date: '',
    returnDate: '',
    passengers: 1
  });
  const [isRoundTrip, setIsRoundTrip] = useState(false);
  const [results, setResults] = useState([]);
  const [returnResults, setReturnResults] = useState([]);
  const [searched, setSearched] = useState(false);
  
  // Booking settings (fetched from backend)
  const [bookingSettings, setBookingSettings] = useState({
    roundTripDiscount: 5,
    serviceFee: 20,
    maxPassengersPerBooking: 10
  });
  
  // Price estimator state
  const [expandedTrip, setExpandedTrip] = useState(null);
  const [tripDestinations, setTripDestinations] = useState({});
  const [loadingDestinations, setLoadingDestinations] = useState({});
  
  // Waitlist state
  const [waitlistStatus, setWaitlistStatus] = useState({});
  const [joiningWaitlist, setJoiningWaitlist] = useState({});

  // Fetch booking settings on mount
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await api.get('/bus/settings');
        if (response.data.settings) {
          setBookingSettings(response.data.settings);
        }
      } catch (error) {
        console.error('Failed to fetch booking settings:', error);
      }
    };
    fetchSettings();
  }, []);

  // Popular routes in Zambia
  const popularRoutes = [
    { from: 'Lusaka', to: 'Livingstone', emoji: '🦁' },
    { from: 'Lusaka', to: 'Ndola', emoji: '🏭' },
    { from: 'Lusaka', to: 'Kitwe', emoji: '⛏️' },
    { from: 'Lusaka', to: 'Chipata', emoji: '🌿' },
    { from: 'Lusaka', to: 'Solwezi', emoji: '🪨' },
    { from: 'Ndola', to: 'Kasama', emoji: '🌊' }
  ];

  const getAmenityIcon = (amenity) => {
    switch (amenity.toLowerCase()) {
      case 'wifi': return <Wifi className="h-4 w-4" />;
      case 'ac': case 'air conditioning': return <Wind className="h-4 w-4" />;
      case 'tv': case 'entertainment': return <Tv className="h-4 w-4" />;
      case 'refreshments': return <Coffee className="h-4 w-4" />;
      default: return null;
    }
  };

  const handleSearch = async (e) => {
    e?.preventDefault();
    
    if (!searchParams.origin || !searchParams.destination || !searchParams.date) {
      toast({
        title: t('error.missingInfo'),
        description: t('error.fillFields'),
        variant: 'destructive'
      });
      return;
    }
    
    if (isRoundTrip && !searchParams.returnDate) {
      toast({
        title: t('error.missingInfo'),
        description: 'Please select a return date',
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);
    setSearched(true);
    
    try {
      // Search outbound trips
      const response = await api.get('/bus/search', {
        params: {
          origin: searchParams.origin,
          destination: searchParams.destination,
          date: searchParams.date,
          passengers: searchParams.passengers
        }
      });
      
      setResults(response.data.results || []);
      
      // Search return trips if round trip
      if (isRoundTrip && searchParams.returnDate) {
        const returnResponse = await api.get('/bus/search', {
          params: {
            origin: searchParams.destination,
            destination: searchParams.origin,
            date: searchParams.returnDate,
            passengers: searchParams.passengers
          }
        });
        setReturnResults(returnResponse.data.results || []);
      } else {
        setReturnResults([]);
      }
      
      if (response.data.results?.length === 0) {
        toast({
          title: 'No Buses Found',
          description: `No buses available from ${searchParams.origin} to ${searchParams.destination} on ${searchParams.date}`,
        });
      }
    } catch (error) {
      console.error('Search error:', error);
      toast({
        title: 'Search Error',
        description: error.response?.data?.detail || t('error.searchFailed'),
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTrip = (trip, isReturn = false) => {
    if (isRoundTrip && !isReturn) {
      // For round trip, store outbound and navigate to select return
      navigate('/bus/seats', { 
        state: { 
          trip, 
          passengers: searchParams.passengers,
          isRoundTrip: true,
          returnDate: searchParams.returnDate,
          returnResults: returnResults
        } 
      });
    } else {
      navigate('/bus/seats', { state: { trip, passengers: searchParams.passengers } });
    }
  };

  const handlePopularRoute = (route) => {
    setSearchParams(prev => ({
      ...prev,
      origin: route.from,
      destination: route.to
    }));
  };
  
  // Swap origin and destination
  const swapLocations = () => {
    setSearchParams(prev => ({
      ...prev,
      origin: prev.destination,
      destination: prev.origin
    }));
  };
  
  // Join waitlist for sold-out trip
  const handleJoinWaitlist = async (tripId, e) => {
    e.stopPropagation();
    
    const token = localStorage.getItem('tekstar_token');
    if (!token) {
      toast({
        title: t('error.loginRequired'),
        description: t('error.pleaseLogin'),
        variant: 'destructive'
      });
      navigate('/login', { state: { returnTo: '/bus' } });
      return;
    }
    
    setJoiningWaitlist(prev => ({ ...prev, [tripId]: true }));
    
    try {
      await api.post(`/bus/waitlist/${tripId}?passengers=${searchParams.passengers}`);
      setWaitlistStatus(prev => ({ ...prev, [tripId]: true }));
      toast({
        title: t('waitlist.joined'),
        description: t('waitlist.notify'),
      });
    } catch (error) {
      if (error.response?.status === 400) {
        setWaitlistStatus(prev => ({ ...prev, [tripId]: true }));
        toast({
          title: t('waitlist.already'),
          variant: 'destructive'
        });
      } else {
        toast({
          title: 'Error',
          description: 'Failed to join waitlist',
          variant: 'destructive'
        });
      }
    } finally {
      setJoiningWaitlist(prev => ({ ...prev, [tripId]: false }));
    }
  };
  
  // Leave waitlist
  const handleLeaveWaitlist = async (tripId, e) => {
    e.stopPropagation();
    
    try {
      await api.delete(`/bus/waitlist/${tripId}`);
      setWaitlistStatus(prev => ({ ...prev, [tripId]: false }));
      toast({
        title: 'Removed from waitlist',
      });
    } catch (error) {
      console.error('Failed to leave waitlist:', error);
    }
  };

  // Fetch destinations for price estimator
  const togglePriceEstimator = async (tripId, e) => {
    e.stopPropagation(); // Prevent card click
    
    if (expandedTrip === tripId) {
      setExpandedTrip(null);
      return;
    }
    
    setExpandedTrip(tripId);
    
    // If already fetched, don't fetch again
    if (tripDestinations[tripId]) return;
    
    setLoadingDestinations(prev => ({ ...prev, [tripId]: true }));
    
    try {
      const response = await api.get(`/bus/trips/${tripId}/destinations`);
      setTripDestinations(prev => ({
        ...prev,
        [tripId]: response.data.destinations || []
      }));
    } catch (error) {
      console.error('Failed to fetch destinations:', error);
      toast({
        title: 'Error',
        description: 'Failed to load stop prices',
        variant: 'destructive'
      });
    } finally {
      setLoadingDestinations(prev => ({ ...prev, [tripId]: false }));
    }
  };

  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  // Get min date (today)
  const today = new Date().toISOString().split('T')[0];
  
  // Render trip card
  const renderTripCard = (trip, isReturn = false) => {
    const isSoldOut = trip.availableSeats === 0;
    const isOnWaitlist = waitlistStatus[trip.tripId];
    
    return (
      <Card 
        key={trip.tripId}
        className={`p-4 transition-all ${isSoldOut ? 'opacity-75' : 'hover:border-blue-300'}`}
        data-testid={`trip-${trip.tripId}`}
      >
        <div 
          className={`flex items-start justify-between ${!isSoldOut ? 'cursor-pointer' : ''}`}
          onClick={() => !isSoldOut && handleSelectTrip(trip, isReturn)}
        >
          <div className="flex-1">
            {/* Operator Info */}
            <div className="flex items-center gap-2 mb-3">
              <span className="text-2xl">🚌</span>
              <div>
                <h3 className="font-semibold text-lg">{trip.operatorName}</h3>
                <div className="flex items-center gap-1 text-sm text-gray-500">
                  <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                  <span>{trip.operatorRating?.toFixed(1) || 'New'}</span>
                  <span className="mx-1">•</span>
                  <span>{trip.bus?.make} {trip.bus?.model}</span>
                </div>
              </div>
            </div>

            {/* Time & Route */}
            <div className="flex items-center gap-4 mb-3">
              <div className="text-center">
                <p className="text-2xl font-bold">{trip.departureTime}</p>
                <p className="text-sm text-gray-500">{trip.route?.origin?.city}</p>
              </div>
              <div className="flex-1 flex items-center gap-2">
                <div className="flex-1 border-t-2 border-dashed border-gray-300" />
                <div className="flex items-center gap-1 text-sm text-gray-500">
                  <Clock className="h-4 w-4" />
                  {formatDuration(trip.durationMinutes)}
                </div>
                <div className="flex-1 border-t-2 border-dashed border-gray-300" />
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold">{trip.arrivalTime}</p>
                <p className="text-sm text-gray-500">{trip.route?.destination?.city}</p>
              </div>
            </div>

            {/* Amenities */}
            {trip.amenities?.length > 0 && (
              <div className="flex items-center gap-3 text-sm text-gray-500">
                {trip.amenities.slice(0, 4).map((amenity, idx) => (
                  <div key={idx} className="flex items-center gap-1">
                    {getAmenityIcon(amenity)}
                    <span>{amenity}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Price & Book */}
          <div className="text-right ml-4">
            <p className="text-sm text-gray-500">{t('results.from')}</p>
            <p className="text-2xl font-bold text-blue-600">
              K{trip.pricing?.standard?.toFixed(0) || '0'}
            </p>
            <p className="text-sm text-gray-500 mb-2">{t('results.perSeat')}</p>
            
            {isSoldOut ? (
              <>
                <div className="text-sm text-red-500 font-medium mb-2">
                  {t('results.soldOut')}
                </div>
                {isOnWaitlist ? (
                  <Button 
                    size="sm" 
                    variant="outline"
                    className="border-amber-500 text-amber-600"
                    onClick={(e) => handleLeaveWaitlist(trip.tripId, e)}
                    data-testid={`leave-waitlist-${trip.tripId}`}
                  >
                    <BellOff className="h-4 w-4 mr-1" />
                    {t('results.onWaitlist')}
                  </Button>
                ) : (
                  <Button 
                    size="sm" 
                    variant="outline"
                    className="border-blue-500 text-blue-600"
                    onClick={(e) => handleJoinWaitlist(trip.tripId, e)}
                    disabled={joiningWaitlist[trip.tripId]}
                    data-testid={`join-waitlist-${trip.tripId}`}
                  >
                    {joiningWaitlist[trip.tripId] ? (
                      <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                    ) : (
                      <Bell className="h-4 w-4 mr-1" />
                    )}
                    {t('results.joinWaitlist')}
                  </Button>
                )}
              </>
            ) : (
              <>
                <div className="flex items-center gap-1 text-sm text-green-600">
                  <span>{t('results.seatsLeft', { count: trip.availableSeats })}</span>
                </div>
                <Button size="sm" className="mt-2 bg-blue-600 hover:bg-blue-700">
                  {t('results.select')}
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Price Estimator Toggle */}
        <div className="mt-3 pt-3 border-t">
          <button
            onClick={(e) => togglePriceEstimator(trip.tripId, e)}
            className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 transition-colors"
            data-testid={`price-estimator-toggle-${trip.tripId}`}
          >
            <Navigation className="h-4 w-4" />
            <span>{t('price.viewAllStops')}</span>
            {expandedTrip === trip.tripId ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </button>
          
          {/* Expanded Price Estimator */}
          {expandedTrip === trip.tripId && (
            <div className="mt-3 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg" data-testid="price-estimator-panel">
              {loadingDestinations[trip.tripId] ? (
                <div className="flex items-center justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
                  <span className="ml-2 text-sm text-gray-500">Loading prices...</span>
                </div>
              ) : tripDestinations[trip.tripId]?.length > 0 ? (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Info className="h-4 w-4 text-blue-600" />
                    <span className="text-sm text-gray-600">
                      {t('price.alightAnyStop')}
                    </span>
                  </div>
                  <div className="space-y-2">
                    {tripDestinations[trip.tripId].map((dest, idx) => (
                      <div 
                        key={idx}
                        className={`flex items-center justify-between p-2 rounded ${
                          dest.isFinalDestination 
                            ? 'bg-blue-100 border border-blue-200' 
                            : 'bg-white border border-gray-100'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${
                            dest.isFinalDestination ? 'bg-blue-600' : 'bg-amber-500'
                          }`} />
                          <span className="font-medium">{dest.city}</span>
                          {dest.isFinalDestination && (
                            <span className="text-xs bg-blue-600 text-white px-2 py-0.5 rounded">
                              {t('price.final')}
                            </span>
                          )}
                          {!dest.isFinalDestination && (
                            <span className="text-xs text-gray-500">
                              ({Math.round(dest.pricing?.priceMultiplier * 100)}% {t('price.ofJourney')})
                            </span>
                          )}
                        </div>
                        <div className="text-right">
                          <span className="font-bold text-blue-600">
                            K{Math.round(dest.pricing?.standard || 0)}
                          </span>
                          <span className="text-xs text-gray-500 ml-1">{t('price.standard')}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="mt-3 text-xs text-gray-500 text-center">
                    {t('price.selectDestination')}
                  </p>
                </div>
              ) : (
                <p className="text-sm text-gray-500 text-center py-2">
                  No intermediate stops available
                </p>
              )}
            </div>
          )}
        </div>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <Bus className="h-8 w-8" />
              <h1 className="text-2xl font-bold">{t('app.title')}</h1>
            </div>
            <div className="flex items-center gap-3">
              <LanguageSelector />
              <Link to="/bus/planner" data-testid="multi-city-planner-link">
                <Button variant="outline" className="bg-white/10 border-white/20 text-white hover:bg-white/20">
                  <Navigation className="h-4 w-4 mr-2" />
                  Multi-City
                </Button>
              </Link>
              <Link to="/bus/tickets">
                <Button variant="outline" className="bg-white/10 border-white/20 text-white hover:bg-white/20">
                  <Ticket className="h-4 w-4 mr-2" />
                  {t('nav.myTickets')}
                </Button>
              </Link>
            </div>
          </div>
          <p className="text-blue-100">{t('app.subtitle')}</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4">
        {/* Search Form */}
        <Card className="p-6 -mt-6 shadow-lg" data-testid="bus-search-form">
          <form onSubmit={handleSearch}>
            {/* Round Trip Toggle */}
            <div className="flex items-center justify-center gap-4 mb-4">
              <button
                type="button"
                onClick={() => setIsRoundTrip(false)}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  !isRoundTrip 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                data-testid="one-way-toggle"
              >
                {t('search.oneWay')}
              </button>
              <button
                type="button"
                onClick={() => setIsRoundTrip(true)}
                className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                  isRoundTrip 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                data-testid="round-trip-toggle"
              >
                <ArrowLeftRight className="h-4 w-4" />
                {t('search.roundTrip')}
              </button>
              {isRoundTrip && (
                <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                  {bookingSettings.roundTripDiscount}% off return journey
                </span>
              )}
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 relative">
              <div>
                <Label className="flex items-center gap-2 mb-2">
                  <MapPin className="h-4 w-4 text-green-500" />
                  {t('search.from')}
                </Label>
                <Input
                  value={searchParams.origin}
                  onChange={(e) => setSearchParams({ ...searchParams, origin: e.target.value })}
                  placeholder={t('search.departureCity')}
                  className="text-lg"
                  data-testid="origin-input"
                />
              </div>
              
              {/* Swap Button */}
              <button
                type="button"
                onClick={swapLocations}
                className="absolute left-1/2 top-1/2 -translate-x-1/2 translate-y-1 z-10 p-2 bg-white rounded-full shadow-md border hover:bg-gray-50 transition-colors hidden md:block"
                title="Swap locations"
              >
                <ArrowLeftRight className="h-4 w-4 text-gray-500" />
              </button>
              
              <div>
                <Label className="flex items-center gap-2 mb-2">
                  <MapPin className="h-4 w-4 text-red-500" />
                  {t('search.to')}
                </Label>
                <Input
                  value={searchParams.destination}
                  onChange={(e) => setSearchParams({ ...searchParams, destination: e.target.value })}
                  placeholder={t('search.destinationCity')}
                  className="text-lg"
                  data-testid="destination-input"
                />
              </div>
            </div>

            <div className={`grid gap-4 mb-4 ${isRoundTrip ? 'grid-cols-3' : 'grid-cols-2'}`}>
              <div>
                <Label className="flex items-center gap-2 mb-2">
                  <Calendar className="h-4 w-4 text-blue-500" />
                  {t('search.travelDate')}
                </Label>
                <Input
                  type="date"
                  value={searchParams.date}
                  onChange={(e) => setSearchParams({ ...searchParams, date: e.target.value })}
                  min={today}
                  className="text-lg"
                  data-testid="date-input"
                />
              </div>
              
              {isRoundTrip && (
                <div>
                  <Label className="flex items-center gap-2 mb-2">
                    <Calendar className="h-4 w-4 text-purple-500" />
                    {t('search.returnDate')}
                  </Label>
                  <Input
                    type="date"
                    value={searchParams.returnDate}
                    onChange={(e) => setSearchParams({ ...searchParams, returnDate: e.target.value })}
                    min={searchParams.date || today}
                    className="text-lg"
                    data-testid="return-date-input"
                  />
                </div>
              )}
              
              <div>
                <Label className="flex items-center gap-2 mb-2">
                  <Users className="h-4 w-4 text-purple-500" />
                  {t('search.passengers')}
                </Label>
                <Input
                  type="number"
                  value={searchParams.passengers}
                  onChange={(e) => setSearchParams({ ...searchParams, passengers: parseInt(e.target.value) || 1 })}
                  min={1}
                  max={10}
                  className="text-lg"
                  data-testid="passengers-input"
                />
              </div>
            </div>

            <Button 
              type="submit"
              className="w-full bg-blue-600 hover:bg-blue-700 text-lg py-6"
              disabled={loading}
              data-testid="search-btn"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
                  {t('search.searching')}
                </>
              ) : (
                <>
                  <Search className="mr-2 h-5 w-5" />
                  {t('search.searchBuses')}
                </>
              )}
            </Button>
          </form>
        </Card>

        {/* Popular Routes */}
        {!searched && (
          <div className="mt-6">
            <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
              <Star className="h-5 w-5 text-yellow-500" />
              {t('popular.title')}
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {popularRoutes.map((route, idx) => (
                <Card 
                  key={idx}
                  className="p-4 cursor-pointer hover:border-blue-300 hover:bg-blue-50 transition-all"
                  onClick={() => handlePopularRoute(route)}
                  data-testid={`popular-route-${idx}`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">{route.emoji}</span>
                    <div>
                      <p className="font-medium">{route.from}</p>
                      <p className="text-sm text-gray-500 flex items-center gap-1">
                        <ArrowRight className="h-3 w-3" />
                        {route.to}
                      </p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Search Results */}
        {searched && (
          <div className="mt-6 space-y-4" data-testid="search-results">
            <h2 className="text-lg font-semibold">
              {results.length > 0 
                ? `${results.length} bus${results.length > 1 ? 'es' : ''} found`
                : 'No buses found'
              }
            </h2>

            {results.length === 0 && (
              <Card className="p-8 text-center">
                <Bus className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-600 mb-2">{t('results.noBuses')}</h3>
                <p className="text-gray-500 mb-4">
                  {t('results.tryDifferent')}
                </p>
                <Button variant="outline" onClick={() => setSearched(false)}>
                  {t('results.searchAgain')}
                </Button>
              </Card>
            )}

            {/* Outbound Trips */}
            {results.length > 0 && (
              <div className="space-y-4">
                {isRoundTrip && (
                  <div className="flex items-center gap-2 text-sm font-medium text-gray-600 bg-blue-50 p-3 rounded-lg">
                    <ArrowRight className="h-4 w-4" />
                    <span>Outbound: {searchParams.origin} → {searchParams.destination} ({searchParams.date})</span>
                  </div>
                )}
                {results.map((trip) => renderTripCard(trip, false))}
              </div>
            )}
            
            {/* Return Trips */}
            {isRoundTrip && returnResults.length > 0 && (
              <div className="mt-8 space-y-4">
                <div className="flex items-center gap-2 text-sm font-medium text-gray-600 bg-purple-50 p-3 rounded-lg">
                  <ArrowLeft className="h-4 w-4" />
                  <span>Return: {searchParams.destination} → {searchParams.origin} ({searchParams.returnDate})</span>
                  <span className="ml-auto text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                    {bookingSettings.roundTripDiscount}% off return journey
                  </span>
                </div>
                {returnResults.map((trip) => renderTripCard(trip, true))}
              </div>
            )}
          </div>
        )}

        {/* Operator CTA */}
        <Card className="mt-8 p-6 bg-gradient-to-r from-indigo-500 to-purple-500 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-bold mb-1">{t('operator.cta.title')}</h3>
              <p className="text-indigo-100">{t('operator.cta.subtitle')}</p>
            </div>
            <Link to="/bus/operator/apply">
              <Button className="bg-white text-indigo-600 hover:bg-indigo-50">
                {t('operator.cta.button')}
              </Button>
            </Link>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default BusSearch;
